// A friend's own video or music file, from the friend's phone to the DJ Booth screen. Nothing is stored anywhere: the file travels
// through a WebRTC data channel (an encrypted line between the two browsers). When the two cannot reach each other directly (different
// networks, other towns, other countries) the same encrypted line goes through Cloudflare's relay, which the server hands out
// (backend/dj.py, /dj/screen/ice and /dj/party/ice).
//
// The two browsers only need to find each other first. They do that by passing short "hello" messages (offer, answer, ice) through the
// server (/dj/screen/rtc, /dj/party/rtc and their /wait calls); the file itself never touches the server.
//   * The SCREEN asks for the file (it makes the offer and the data channel): receiveFromPhone / fetchFromPhone.
//   * The FRIEND's phone answers and sends it in small pieces: sendToScreen / startPhoneSender.
// The screen holds the pieces until it has all of them, checks the total, and only then makes the file (a Blob) that the <video> plays.
// Pages: pages/DjScreen.js (screen) and pages/DjJoin.js (friend).

export const CHUNK = 16 * 1024;                 // the size every browser accepts for one message on a data channel
export const BLOCK = 1024 * 1024;               // how much of the file is read from the phone at a time
const HIGH = 4 * 1024 * 1024;                   // the phone waits when this much is queued on the channel...
const LOW = 1024 * 1024;                        // ...and goes on when it is down to this much
export const MAX_BYTES = 300 * 1024 * 1024;     // same as the server (backend/dj.py MAX_PHONE_BYTES)

export class XferError extends Error {
  constructor(why) { super(why); this.why = why; }
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const noop = () => {};

export function newXferId() {
  try {
    const a = new Uint8Array(9);
    crypto.getRandomValues(a);
    return Array.from(a, (x) => (x % 36).toString(36)).join("");
  } catch {
    return Math.random().toString(36).slice(2, 11).padEnd(9, "x");
  }
}

// The words for "why did it not work", for the screen's note and the friend's page.
export function whyText(why, relay) {
  switch (why) {
    case "connect":
      return relay === false
        ? "Could not connect your phone to the TV. This can happen when your phone and the TV are on different networks. Try again, or ask the host about the relay."
        : "Could not connect your phone to the TV. Check your signal and try again.";
    case "stalled": return "The connection dropped while the file was on its way. Keep this page open and try again.";
    case "size": return "The file did not match what was announced. Pick it again and try again.";
    case "short": return "Part of the file got lost on the way. Try again.";
    case "gone": return "The file is no longer on this page (the page was reloaded?). Choose it again.";
    case "lost": return "The TV lost the file (its page was reloaded?). Load it again.";
    case "unsupported": return "This phone or browser cannot send files to the TV.";
    default: return "The file did not get to the TV. Try again.";
  }
}

// ---------------------------------------------------------------------------
// The screen's side: ask one friend's phone for one file
// ---------------------------------------------------------------------------
// signal = { send(kind, data) -> Promise, wait() -> Promise<[{xfer, kind, data}]> } for this one try.
export function receiveFromPhone({ signal, ice, size, mime, onProgress = noop, connectMs = 25000, stallMs = 20000 }) {
  let pc = null;
  let dc = null;
  let dead = false;
  let timer = null;
  let got = 0;
  let doneSeen = false;
  let remoteSet = false;
  const parts = [];
  const early = [];
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });

  const finish = (err, blob) => {
    if (dead) return;
    dead = true;
    clearTimeout(timer);
    const close = () => { try { dc && dc.close(); } catch { /* ignore */ } try { pc && pc.close(); } catch { /* ignore */ } };
    if (err) { close(); parts.length = 0; reject(err instanceof XferError ? err : new XferError("connect")); }
    else { setTimeout(close, 800); resolve(blob); }              // a moment for the "got it" note to reach the phone
  };
  const arm = (ms, why) => { clearTimeout(timer); timer = setTimeout(() => finish(new XferError(why)), ms); };

  const onMessage = (ev) => {
    if (dead) return;
    if (typeof ev.data === "string") {
      let m = null;
      try { m = JSON.parse(ev.data); } catch { return; }
      if (!m) return;
      if (m.t === "meta") {
        if (Number(m.size) !== size) finish(new XferError("size"));
      } else if (m.t === "err") {
        finish(new XferError(m.why === "gone" ? "gone" : "unsupported"));
      } else if (m.t === "done") {
        doneSeen = true;
        if (got !== size) { finish(new XferError("short")); return; }
        try { dc.send(JSON.stringify({ t: "got" })); } catch { /* the phone will find out from the server */ }
        finish(null, new Blob(parts.splice(0), { type: mime === "video/quicktime" ? "video/mp4" : mime }));
      }
      return;
    }
    const buf = ev.data;
    if (!buf || typeof buf.byteLength !== "number") return;
    got += buf.byteLength;
    if (got > size) { finish(new XferError("size")); return; }
    parts.push(buf);
    arm(stallMs, "stalled");
    onProgress(got / size);
  };

  const listen = async () => {
    while (!dead) {
      let msgs = [];
      try { msgs = (await signal.wait()) || []; } catch { await sleep(1000); continue; }
      for (const m of msgs) {
        if (dead) return;
        try {
          if (m.kind === "answer") {
            await pc.setRemoteDescription(m.data);
            remoteSet = true;
            while (early.length) await pc.addIceCandidate(early.shift());
          } else if (m.kind === "ice") {
            if (remoteSet) await pc.addIceCandidate(m.data); else early.push(m.data);
          } else if (m.kind === "bye") {
            finish(new XferError("gone"));
            return;
          }
        } catch { /* a message this browser does not like: skip it */ }
      }
    }
  };

  (async () => {
    try {
      pc = new RTCPeerConnection({ iceServers: ice });
      dc = pc.createDataChannel("file", { ordered: true });
      dc.binaryType = "arraybuffer";
      arm(connectMs, "connect");
      dc.onopen = () => arm(stallMs, "stalled");
      dc.onmessage = onMessage;
      dc.onclose = () => { if (!dead && !doneSeen) finish(new XferError("stalled")); };
      const pending = [];
      let offerSent = false;
      pc.onicecandidate = (e) => {
        if (!e.candidate) return;
        const c = e.candidate.toJSON();
        if (offerSent) signal.send("ice", c).catch(noop); else pending.push(c);
      };
      pc.onconnectionstatechange = () => { if (pc.connectionState === "failed") finish(new XferError("connect")); };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await signal.send("offer", { type: offer.type, sdp: offer.sdp });
      offerSent = true;
      pending.splice(0).forEach((c) => signal.send("ice", c).catch(noop));
      listen();
    } catch (e) {
      finish(e instanceof XferError ? e : new XferError(typeof RTCPeerConnection === "undefined" ? "unsupported" : "connect"));
    }
  })();

  return { promise, cancel: (why) => finish(new XferError(why || "gone")) };
}

// Fetches one friend's file for the screen, trying again (up to `attempts` tries) when the line could not be made or dropped.
// `api` is the site's axios helper. `isWanted()` says whether any deck still wants this file (so a deck that was emptied stops the work).
export function fetchFromPhone({ api, code, token, fid, size, mime, onProgress = noop, isWanted = () => true, attempts = 3 }) {
  let current = null;
  let cancelled = false;
  const promise = (async () => {
    let last = new XferError("connect");
    for (let i = 0; i < attempts && !cancelled && isWanted(); i += 1) {
      const xfer = newXferId();
      try {
        const r = await api.get("/dj/screen/ice", { params: { code, token }, timeout: 10000 });
        const signal = {
          send: (kind, data) => api.post("/dj/screen/rtc", { code, token, to: fid, xfer, kind, data }, { timeout: 10000 }),
          wait: async () => (await api.get("/dj/screen/rtc/wait", { params: { code, token, xfer }, timeout: 25000 })).data.msgs,
        };
        current = receiveFromPhone({ signal, ice: r.data.servers, size, mime, onProgress });
        const blob = await current.promise;
        signal.send("bye", {}).catch(noop);
        return { blob, relay: r.data.relay };
      } catch (e) {
        last = e instanceof XferError ? e : new XferError(e && e.response && e.response.status === 403 ? "gone" : "connect");
        if (["size", "gone", "unsupported"].includes(last.why)) break;
        await sleep(800);
      }
    }
    throw last;
  })();
  return { promise, cancel: () => { cancelled = true; if (current) current.cancel("gone"); } };
}

// ---------------------------------------------------------------------------
// The friend's side: answer the screen and send the file
// ---------------------------------------------------------------------------
// `offer` is what the screen sent. `file` is the File the friend picked. Messages that follow (ice, bye) go to `handle`.
export function sendToScreen({ signal, ice, offer, file, onProgress = noop, onPhase = noop, connectMs = 30000 }) {
  let pc = null;
  let dc = null;
  let dead = false;
  let remoteSet = false;
  let finishing = false;
  let timer = null;
  const early = [];
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });

  const finish = (err) => {
    if (dead) return;
    dead = true;
    clearTimeout(timer);
    try { dc && dc.close(); } catch { /* ignore */ }
    try { pc && pc.close(); } catch { /* ignore */ }
    if (err) reject(err instanceof XferError ? err : new XferError("stalled")); else resolve();
  };
  const arm = (ms, why) => { clearTimeout(timer); timer = setTimeout(() => finish(new XferError(why)), ms); };

  const drain = () => new Promise((res, rej) => {
    let poll = null;
    const done = () => { clearInterval(poll); dc.removeEventListener("bufferedamountlow", ok); dc.removeEventListener("close", bad); };
    const ok = () => { done(); res(); };
    const bad = () => { done(); rej(new XferError("stalled")); };
    dc.addEventListener("bufferedamountlow", ok);
    dc.addEventListener("close", bad);
    poll = setInterval(() => { if (dc.readyState !== "open") bad(); else if (dc.bufferedAmount <= LOW) ok(); }, 1000);
  });

  const stream = async () => {
    try {
      clearTimeout(timer);
      onPhase("sending");
      dc.bufferedAmountLowThreshold = LOW;
      dc.send(JSON.stringify({ t: "meta", size: file.size }));
      let off = 0;
      while (off < file.size) {
        const end = Math.min(off + BLOCK, file.size);
        const buf = await file.slice(off, end).arrayBuffer();
        for (let p = 0; p < buf.byteLength; p += CHUNK) {
          if (dead || dc.readyState !== "open") throw new XferError("stalled");
          if (dc.bufferedAmount > HIGH) await drain();
          dc.send(new Uint8Array(buf, p, Math.min(CHUNK, buf.byteLength - p)));
        }
        off = end;
        onProgress(Math.max(0, off - dc.bufferedAmount) / file.size);
      }
      finishing = true;
      onPhase("finishing");
      dc.send(JSON.stringify({ t: "done" }));
      arm(120000, "stalled");                       // the screen says "got it" (or closes the line) when it has everything
    } catch (e) {
      finish(e instanceof XferError ? e : new XferError("stalled"));
    }
  };

  (async () => {
    try {
      pc = new RTCPeerConnection({ iceServers: ice });
      arm(connectMs, "connect");
      onPhase("connecting");
      const pending = [];
      let answerSent = false;
      pc.onicecandidate = (e) => {
        if (!e.candidate) return;
        const c = e.candidate.toJSON();
        if (answerSent) signal.send("ice", c).catch(noop); else pending.push(c);
      };
      pc.onconnectionstatechange = () => { if (pc.connectionState === "failed") finish(new XferError("connect")); };
      pc.ondatachannel = (ev) => {
        dc = ev.channel;
        dc.binaryType = "arraybuffer";
        dc.onopen = () => { stream(); };
        dc.onclose = () => { if (!dead) finish(finishing ? null : new XferError("stalled")); };    // after "done" a closing line means the screen has it
        dc.onmessage = (m) => {
          if (typeof m.data !== "string") return;
          try { if (JSON.parse(m.data).t === "got") finish(null); } catch { /* ignore */ }
        };
      };
      await pc.setRemoteDescription(offer);
      remoteSet = true;
      while (early.length) await pc.addIceCandidate(early.shift());
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await signal.send("answer", { type: answer.type, sdp: answer.sdp });
      answerSent = true;
      pending.splice(0).forEach((c) => signal.send("ice", c).catch(noop));
    } catch (e) {
      finish(e instanceof XferError ? e : new XferError(typeof RTCPeerConnection === "undefined" ? "unsupported" : "connect"));
    }
  })();

  const handle = (m) => {
    if (dead) return;
    if (m.kind === "ice") {
      if (remoteSet) pc.addIceCandidate(m.data).catch(noop); else early.push(m.data);
    } else if (m.kind === "bye") {
      finish(new XferError("gone"));
    }
  };
  return { promise, handle, close: () => finish(new XferError("gone")) };
}

// Listens on the friend's phone for the screen asking for the file that is on the friend's deck, and sends it.
//   getFile()   -> the File to send, or null when this page does not have it (the page was reloaded)
//   onInfo(i)   -> { phase: "waiting" | "connecting" | "sending" | "finishing" | "done" | "failed", pct?, why?, relay? }
// stop() ends it. The caller starts it while the friend's file waits on a deck and stops it when it no longer does.
export function startPhoneSender({ api, invite, getFile, onInfo = noop }) {
  let stopped = false;
  let session = null;
  let sessionXfer = "";
  let relay = null;
  const stash = new Map();                         // hello messages that came before their offer: xfer -> [msg]
  const postHello = (xfer, kind, data) => api.post("/dj/party/rtc", { invite, xfer, kind, data }, { timeout: 10000 });

  const begin = async (m) => {
    if (session) { session.close(); session = null; }
    const file = getFile();
    if (!file) { postHello(m.xfer, "bye", {}).catch(noop); onInfo({ phase: "failed", why: "gone" }); return; }
    let ice;
    try {
      const r = await api.get("/dj/party/ice", { params: { invite }, timeout: 10000 });
      ice = r.data.servers;
      relay = r.data.relay;
    } catch {
      postHello(m.xfer, "bye", {}).catch(noop);
      onInfo({ phase: "failed", why: "connect" });
      return;
    }
    if (stopped) return;
    const signal = { send: (kind, data) => postHello(m.xfer, kind, data) };
    sessionXfer = m.xfer;
    onInfo({ phase: "connecting", relay });
    const s = sendToScreen({
      signal, ice, offer: m.data, file,
      onProgress: (p) => onInfo({ phase: "sending", pct: p, relay }),
      onPhase: (phase) => onInfo({ phase, relay }),
    });
    session = s;
    (stash.get(m.xfer) || []).forEach((x) => s.handle(x));
    stash.delete(m.xfer);
    s.promise.then(() => { if (session === s) onInfo({ phase: "done", relay }); })
      .catch((e) => { if (session === s) onInfo({ phase: "failed", why: e && e.why ? e.why : "stalled", relay }); });
  };

  (async () => {
    onInfo({ phase: "waiting" });
    while (!stopped) {
      let msgs = [];
      try {
        msgs = (await api.get("/dj/party/rtc/wait", { params: { invite }, timeout: 30000 })).data.msgs || [];
      } catch { if (stopped) return; await sleep(1500); continue; }
      for (const m of msgs) {
        if (stopped) return;
        if (m.kind === "offer") await begin(m);
        else if (session && m.xfer === sessionXfer) session.handle(m);
        else if (stash.size < 8) stash.set(m.xfer, [...(stash.get(m.xfer) || []).slice(-40), m]);
      }
    }
  })();

  return {
    stop: () => { stopped = true; if (session) { session.close(); session = null; } stash.clear(); },
  };
}
